import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMinus, FiPackage, FiShoppingCart } = FiIcons;

const EnhancedInventoryAdjustment = ({ productId }) => {
  const { adjustInventory, getProductById } = useInventory();
  const { user } = useAuth();
  const [adjustment, setAdjustment] = useState({
    quantity: '',
    type: 'purchase',
    reason: '',
    selectedListing: null
  });
  const [showListingSelector, setShowListingSelector] = useState(false);

  const product = getProductById(productId);
  const salesListings = product?.salesListings || [];
  const activeListings = salesListings.filter(listing => listing.status === 'active');

  const adjustmentTypes = [
    { value: 'purchase', label: 'Purchase/Restock', icon: FiPlus, color: 'green' },
    { 
      value: 'sale', 
      label: 'Sale', 
      icon: FiShoppingCart, 
      color: 'blue',
      showListingSelector: true 
    },
    { value: 'damaged', label: 'Damaged', icon: FiMinus, color: 'red' },
    { value: 'internal_use', label: 'Internal Use', icon: FiMinus, color: 'orange' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const quantity = parseInt(adjustment.quantity);
    if (isNaN(quantity) || quantity === 0) return;

    const quantityChange = ['sale', 'damaged', 'internal_use'].includes(adjustment.type) 
      ? -Math.abs(quantity) 
      : Math.abs(quantity);

    // Build enhanced reason string
    let enhancedReason = adjustment.reason || `${adjustment.type} adjustment`;
    if (adjustment.type === 'sale' && adjustment.selectedListing) {
      const listing = activeListings.find(l => l.id === adjustment.selectedListing);
      enhancedReason = `Sold via ${listing?.platform} - ${enhancedReason}`;
    }

    const success = adjustInventory(
      productId,
      quantityChange,
      adjustment.type,
      enhancedReason,
      user?.name || user?.email
    );

    if (success) {
      setAdjustment({
        quantity: '',
        type: 'purchase',
        reason: '',
        selectedListing: null
      });
      setShowListingSelector(false);
    }
  };

  const selectedType = adjustmentTypes.find(type => type.value === adjustment.type);

  const handleTypeChange = (newType) => {
    setAdjustment(prev => ({
      ...prev,
      type: newType,
      selectedListing: null
    }));
    
    const typeConfig = adjustmentTypes.find(t => t.value === newType);
    if (typeConfig?.showListingSelector && activeListings.length > 0) {
      setShowListingSelector(true);
    } else {
      setShowListingSelector(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center mb-6">
        <SafeIcon icon={FiPackage} className="h-5 w-5 text-gray-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Inventory Adjustment</h3>
      </div>
      
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-gray-600">Current Stock</p>
          <p className="text-3xl font-bold text-gray-900">{product?.quantity || 0}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adjustment Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {adjustmentTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => handleTypeChange(type.value)}
                className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all duration-200 ${
                  adjustment.type === type.value 
                    ? `border-${type.color}-500 bg-${type.color}-50 text-${type.color}-700` 
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <SafeIcon icon={type.icon} className="h-4 w-4 mr-2" />
                <span className="text-xs font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sales Listing Selector */}
        {showListingSelector && adjustment.type === 'sale' && activeListings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <label className="block text-sm font-medium text-blue-900 mb-3">
              Select Sales Listing (Optional)
            </label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setAdjustment(prev => ({ ...prev, selectedListing: null }))}
                className={`w-full text-left p-3 rounded-lg border transition-colors duration-200 ${
                  !adjustment.selectedListing
                    ? 'border-blue-500 bg-blue-100 text-blue-900'
                    : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                }`}
              >
                <p className="font-medium">General Sale</p>
                <p className="text-sm opacity-75">Mark as sold without specific listing</p>
              </button>
              {activeListings.map((listing) => (
                <button
                  key={listing.id}
                  type="button"
                  onClick={() => setAdjustment(prev => ({ ...prev, selectedListing: listing.id }))}
                  className={`w-full text-left p-3 rounded-lg border transition-colors duration-200 ${
                    adjustment.selectedListing === listing.id
                      ? 'border-blue-500 bg-blue-100 text-blue-900'
                      : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{listing.platform}</p>
                      <p className="text-sm opacity-75">Listed at ${listing.price.toFixed(2)}</p>
                      {listing.url && (
                        <p className="text-xs opacity-60 truncate">{listing.url}</p>
                      )}
                    </div>
                    <SafeIcon icon={FiShoppingCart} className="h-4 w-4 text-blue-600" />
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity
          </label>
          <input
            type="number"
            value={adjustment.quantity}
            onChange={(e) => setAdjustment(prev => ({ ...prev, quantity: e.target.value }))}
            min="1"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter quantity"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason (Optional)
          </label>
          <input
            type="text"
            value={adjustment.reason}
            onChange={(e) => setAdjustment(prev => ({ ...prev, reason: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Reason for adjustment"
          />
        </div>

        <button
          type="submit"
          disabled={!adjustment.quantity}
          className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          <SafeIcon icon={selectedType?.icon} className="h-4 w-4 mr-2" />
          Apply Adjustment
        </button>
      </form>
    </div>
  );
};

export default EnhancedInventoryAdjustment;